import { Flex, Form, Switch, Tag, Typography } from "antd"
import React, { memo, useMemo } from "react"

import type { NestedValues } from "../helpers/dependencies.ts"
import { type BoundFeature, VIEW_ALL_CODES } from "../helpers/section-layout.ts"
import type {
  ActionRecord,
  OnFeaturesToggle,
  PermissionsMode
} from "../types.ts"
import type { PermissionDisplayMap } from "../types/permissions.types.ts"
import { PermissionsFeatureRow } from "./PermissionsFeatureRow.tsx"

const { Text } = Typography

type PermissionsFeaturesBlockProps = {
  features: BoundFeature[]
  onFeaturesToggle?: OnFeaturesToggle
  isUpdating?: boolean
  disabled?: boolean
  displayMap?: PermissionDisplayMap | null
  modeProps: PermissionsMode
}

const pathOf = (feature: BoundFeature) =>
  `${feature.parentKey}.${feature.subKey}`

export const PermissionsFeaturesBlock = memo(
  ({
    features,
    onFeaturesToggle,
    isUpdating,
    disabled,
    displayMap,
    modeProps
  }: PermissionsFeaturesBlockProps) => {
    const watched = Form.useWatch<NestedValues | undefined>([])
    const valueOf = (feature: BoundFeature) =>
      watched?.[feature.parentKey]?.[feature.subKey]?.[feature.key] ??
      feature.value

    // Toggle all never turns a View All on: that widens what the role sees, and
    // is only ever set by its own switch.
    const bulkFeatures = useMemo(
      () => features.filter((feature) => !VIEW_ALL_CODES.has(feature.key)),
      [features]
    )
    const enabledCount = features.filter((f) => valueOf(f) === true).length
    const allBulkEnabled =
      bulkFeatures.length > 0 && bulkFeatures.every((f) => valueOf(f) === true)

    const toggleAll = (value: boolean) => {
      if (!onFeaturesToggle) return
      const groups = new Map<string, BoundFeature[]>()
      bulkFeatures.forEach((feature) => {
        groups.set(pathOf(feature), [
          ...(groups.get(pathOf(feature)) ?? []),
          feature
        ])
      })
      groups.forEach((group) => {
        const [{ parentKey, subKey }] = group
        const record: ActionRecord = Object.fromEntries(
          group.map((feature) => [feature.key, feature.value])
        )
        onFeaturesToggle(parentKey, record, value, subKey)
      })
    }

    return (
      <div className={"mt-4"}>
        <Flex
          align="center"
          justify="space-between"
          className={"px-4 py-2 bg-background border-b border-border"}
        >
          <Flex align="center" gap={8}>
            <Text type="secondary" className={"uppercase"}>
              Features
            </Text>
            <Tag>
              {enabledCount}/{features.length}
            </Tag>
          </Flex>
          {onFeaturesToggle && bulkFeatures.length > 0 && (
            <Flex
              justify="center"
              align="center"
              gap={6}
              className="px-2 -mr-2 py-1 rounded border border-border shadow-sm"
            >
              <Text
                type="secondary"
                className="text-xs uppercase tracking-[0.04em]"
              >
                Toggle all
              </Text>
              <Switch
                size="small"
                checked={allBulkEnabled}
                onChange={toggleAll}
                disabled={isUpdating || disabled}
              />
            </Flex>
          )}
        </Flex>

        <div
          className={"gap-0 grid"}
          style={{ gridTemplateColumns: "1fr 1fr" }}
        >
          {features.map((feature, idx) => (
            <PermissionsFeatureRow
              key={`${pathOf(feature)}.${feature.key}`}
              parentKey={feature.parentKey}
              subKey={feature.subKey}
              featureKey={feature.key}
              value={feature.value}
              idx={idx}
              isUpdating={isUpdating}
              displayMap={displayMap}
              {...modeProps}
            />
          ))}
        </div>
      </div>
    )
  }
)

PermissionsFeaturesBlock.displayName = "PermissionsFeaturesBlock"
